// === validateWorkflow ===
// Pure, deterministic checker over the InteractivePlugin items[] +
// section.workflow.triggers[] pair. Returns an array of structured issues
// the panel can render in the ValidationDrawer. No React, no Redux, no
// LLM — every rule is a graph traversal or a shape predicate.
//
// This complements (does NOT replace) the AI-side Pedagogy Reviewer added
// in commit 50be5164: the Pedagogy Reviewer evaluates pedagogical quality
// via an LLM; this validator catches structural defects the author can fix
// without any taste-call. They coexist behind different buttons.
//
// Issue shape:
//   {
//     code: "UNREACHABLE_ITEM" | "MISSING_END" | "DANGLING_TRIGGER"
//         | "NO_CORRECT_PATH" | "ORPHANED_BRANCHING_ALT",
//     severity: "warn" | "fix",
//     message: string,
//     targetItemId: string | null,
//     suggestedFix?: {
//       label: string,                            // user-facing button label
//       actionCreator: (ctx) => (dispatch, getState) => void  // thunk factory
//     },
//   }
//
// `suggestedFix.actionCreator` is a factory that takes a context bag
// ({ boxId, sectionId, items, triggers }) and returns a thunk. The drawer
// resolves the context from its connected props at click time and dispatches
// the resulting thunk. We pass context in rather than baking it into the
// issue so validateWorkflow stays a pure function: no Redux dependency, no
// closures over store state captured at validation time.
//
// Reachability model:
//   - Start node = the first item in items[] (matches the synthetic Start
//     edge buildElementGraph emits in CanvasWorkflowPanel).
//   - Default forward edge: each item i -> item i+1, EXCEPT when item i+1
//     is an EndScreen (buildElementGraph skips that edge so authors must
//     route to EndScreen explicitly via a trigger / alternative).
//   - Trigger edges: any trigger whose .actionTargetItemId is a known item.
//   - BranchingQuestion alternative edges: synthesised from the first
//     action whose interactionKind matches a sibling item's kind, mirroring
//     the same lookup buildElementGraph performs.
//
// Action type strings — duplicated here as plain constants (NOT imported)
// so validateWorkflow stays a zero-dep pure module. The drawer's connected
// dispatch sees the same string and routes it through the same reducers.
const ACTION_ADD_INTERACTIVE_ITEM = "ADD_INTERACTIVE_ITEM";
const ACTION_DELETE_WORKFLOW_TRIGGER = "DELETE_WORKFLOW_TRIGGER";
const ACTION_ADD_WORKFLOW_TRIGGER = "ADD_WORKFLOW_TRIGGER";

// Deterministic id generators for fix-emitted items / triggers. Counter-based
// (not Date.now / Math.random) so tests and replays are stable — matches the
// convention used by seedInteractivePluginBoxAndAddItem in common/actions.es6.
let __fixItemCounter = 0;
function nextFixItemId() {
  __fixItemCounter += 1;
  return `it_fix_${__fixItemCounter}`;
}
let __fixTriggerCounter = 0;
function nextFixTriggerId() {
  __fixTriggerCounter += 1;
  return `t_fix_${__fixTriggerCounter}`;
}

// === Fix factories ===
// Each factory returns a thunk `(dispatch, getState) => void` so the drawer
// can dispatch it through redux-thunk. The factories accept a `ctx` bag
// resolved by the drawer at click time so the issue itself stays serialisable
// and the validator stays a pure function.

// MISSING_END — auto-create an EndScreen item on the active InteractivePlugin
// box. Position is a sensible cascade so multiple auto-creates don't stack.
function fixMissingEnd(ctx) {
  return (dispatch) => {
    if (!ctx || !ctx.boxId) return;
    const item = {
      id: nextFixItemId(),
      kind: "EndScreen",
      params: { title: "End", message: "You reached the end of this workflow." },
      position: {
        x: 80 + ((ctx.items && ctx.items.length) || 0) * 24,
        y: 80 + ((ctx.items && ctx.items.length) || 0) * 24,
      },
      source: "validator-fix",
    };
    dispatch({
      type: ACTION_ADD_INTERACTIVE_ITEM,
      payload: { boxId: ctx.boxId, item },
    });
  };
}

// DANGLING_TRIGGER — remove the trigger whose actionTargetItemId is missing.
// `triggerId` is resolved from issue.meta.triggerId set when the issue was
// emitted (the trigger's id is the only reliable handle since the same source
// item can have multiple triggers).
function fixDanglingTrigger(ctx) {
  return (dispatch) => {
    if (!ctx || ctx.sectionId == null || !ctx.triggerId) return;
    dispatch({
      type: ACTION_DELETE_WORKFLOW_TRIGGER,
      payload: { sectionId: ctx.sectionId, triggerId: ctx.triggerId },
    });
  };
}

// ORPHANED_BRANCHING_ALT — wire the offending BranchingQuestion to an
// EndScreen. If no EndScreen exists, auto-create one first and then add an
// onAnswer.correct trigger from the BQ item to that EndScreen.
function fixOrphanedBranchingAlt(ctx) {
  return (dispatch) => {
    if (!ctx || !ctx.boxId || ctx.sectionId == null || !ctx.itemId) return;
    let endItem = (ctx.items || []).find((it) => it.kind === "EndScreen");
    let endId = endItem ? endItem.id : null;
    if (!endId) {
      endId = nextFixItemId();
      dispatch({
        type: ACTION_ADD_INTERACTIVE_ITEM,
        payload: {
          boxId: ctx.boxId,
          item: {
            id: endId,
            kind: "EndScreen",
            params: { title: "End", message: "You reached the end of this workflow." },
            position: { x: 120, y: 120 },
            source: "validator-fix",
          },
        },
      });
    }
    dispatch({
      type: ACTION_ADD_WORKFLOW_TRIGGER,
      payload: {
        sectionId: ctx.sectionId,
        trigger: {
          id: nextFixTriggerId(),
          sceneId: ctx.sceneId || null,
          pluginBoxId: ctx.boxId,
          sourceItemId: ctx.itemId,
          on: "onAnswer.correct",
          actionTargetItemId: endId,
          actions: [],
        },
      },
    });
  };
}

// UNREACHABLE_ITEM — add a trigger from the immediately preceding item in
// items[] to the orphaned one. We pick the closest preceding item because
// it is the most likely intended predecessor; the author can re-route later.
function fixUnreachableItem(ctx) {
  return (dispatch) => {
    if (!ctx || !ctx.boxId || ctx.sectionId == null || !ctx.itemId) return;
    const items = ctx.items || [];
    const idx = items.findIndex((it) => it.id === ctx.itemId);
    if (idx <= 0) return; // no preceding item, nothing to wire from
    const prev = items[idx - 1];
    if (!prev) return;
    dispatch({
      type: ACTION_ADD_WORKFLOW_TRIGGER,
      payload: {
        sectionId: ctx.sectionId,
        trigger: {
          id: nextFixTriggerId(),
          sceneId: ctx.sceneId || null,
          pluginBoxId: ctx.boxId,
          sourceItemId: prev.id,
          on: "onAnswer.correct",
          actionTargetItemId: ctx.itemId,
          actions: [],
        },
      },
    });
  };
}

// Graded kinds — kinds for which NO_CORRECT_PATH applies (an answer-routing
// trigger is expected). Lifted from the per-option / onAnswer handle logic
// in buildElementGraph + ElementNode so the validator can't drift.
const GRADED_KINDS = new Set([
  "MCQ",
  "MultipleChoice",
  "MultipleAnswer",
  "YesNo",
  "TrueFalse",
  "FillInBlank",
  "Ordering",
  "Matching",
  "DragDrop",
]);

// Build adjacency: for each item id, list of item ids reachable via one hop.
// Mirrors buildElementGraph's edge-derivation rules; kept in this file so the
// validator stays a pure function with no graph-builder dependency.
function buildAdjacency(items, triggers) {
  const adj = new Map();
  const itemById = new Map();
  items.forEach((it) => {
    itemById.set(it.id, it);
    adj.set(it.id, new Set());
  });

  // Default next-edges (skip when the successor is an EndScreen — matches
  // the buildElementGraph guard so reachability here agrees with what the
  // panel actually draws).
  for (let i = 0; i < items.length - 1; i++) {
    const a = items[i];
    const b = items[i + 1];
    if (b.kind === "EndScreen") continue;
    adj.get(a.id).add(b.id);
  }

  // BranchingQuestion alternative edges (kind-match lookup).
  items.forEach((item) => {
    if (item.kind !== "BranchingQuestion") return;
    const alts = (item.params && item.params.alternatives) || item.alternatives || [];
    alts.forEach((alt) => {
      const action = (alt.actions || [])[0];
      if (!action || action.kind !== "showInteraction" || !action.interactionKind) return;
      const target = items.find((it) => it.kind === action.interactionKind);
      if (target) adj.get(item.id).add(target.id);
    });
  });

  // Trigger edges — any trigger whose target is a known item.
  triggers.forEach((tr) => {
    if (!tr || !tr.actionTargetItemId) return;
    const sourceItem = items.find((it) => it.id === tr.sourceItemId || it.id === tr.pluginBoxId);
    if (!sourceItem) return;
    if (!itemById.has(tr.actionTargetItemId)) return;
    adj.get(sourceItem.id).add(tr.actionTargetItemId);
  });

  return adj;
}

// Breadth-first reachable set from a single root.
function reachableFrom(adj, rootId) {
  const seen = new Set();
  if (!adj.has(rootId)) return seen;
  const queue = [rootId];
  seen.add(rootId);
  while (queue.length) {
    const id = queue.shift();
    const outs = adj.get(id) || new Set();
    outs.forEach((nxt) => {
      if (!seen.has(nxt)) {
        seen.add(nxt);
        queue.push(nxt);
      }
    });
  }
  return seen;
}

export function validateWorkflow(items, triggers) {
  const safeItems = Array.isArray(items) ? items : [];
  const safeTriggers = Array.isArray(triggers) ? triggers : [];
  const issues = [];

  // Nothing to validate on an empty workflow — the panel renders a placeholder
  // in that state, so silence is the right answer.
  if (safeItems.length === 0) return issues;

  const itemIdSet = new Set(safeItems.map((it) => it.id));
  const startId = safeItems[0].id;
  const adj = buildAdjacency(safeItems, safeTriggers);
  const reachable = reachableFrom(adj, startId);

  // 1. UNREACHABLE_ITEM — items not in the reachable set from Start.
  safeItems.forEach((it) => {
    if (it.id === startId) return;
    if (!reachable.has(it.id)) {
      issues.push({
        code: "UNREACHABLE_ITEM",
        severity: "warn",
        message: `Item "${it.kind}" (${it.id}) is unreachable from Start.`,
        targetItemId: it.id,
        suggestedFix: {
          label: "Connect from previous",
          actionCreator: fixUnreachableItem,
        },
      });
    }
  });

  // 2. MISSING_END — no reachable EndScreen item.
  const endItem = safeItems.find((it) => it.kind === "EndScreen");
  if (!endItem) {
    issues.push({
      code: "MISSING_END",
      severity: "fix",
      message: "Workflow has no EndScreen — learners have no terminal step.",
      targetItemId: null,
      suggestedFix: {
        label: "Auto-create EndScreen",
        actionCreator: fixMissingEnd,
      },
    });
  } else if (!reachable.has(endItem.id)) {
    issues.push({
      code: "MISSING_END",
      severity: "fix",
      message: `EndScreen "${endItem.id}" exists but is unreachable from Start.`,
      targetItemId: endItem.id,
      suggestedFix: {
        label: "Auto-create EndScreen",
        actionCreator: fixMissingEnd,
      },
    });
  }

  // 3. DANGLING_TRIGGER — trigger.actionTargetItemId references a non-existent item.
  safeTriggers.forEach((tr) => {
    if (!tr || !tr.actionTargetItemId) return;
    if (!itemIdSet.has(tr.actionTargetItemId)) {
      issues.push({
        code: "DANGLING_TRIGGER",
        severity: "fix",
        message: `Trigger "${tr.id || "(no-id)"}" points to missing item "${tr.actionTargetItemId}".`,
        targetItemId: tr.sourceItemId || tr.pluginBoxId || null,
        // meta.triggerId lets the fix thunk locate the trigger by id (the
        // sourceItem can have multiple triggers — only the dangling one
        // should be removed).
        meta: { triggerId: tr.id || null },
        suggestedFix: {
          label: "Remove dangling trigger",
          actionCreator: fixDanglingTrigger,
        },
      });
    }
  });

  // 4. NO_CORRECT_PATH — graded items with no onAnswer.correct trigger AND
  //    no per-option correct trigger. We only enforce the onAnswer.correct
  //    leg; an authored per-option route satisfies the rule too.
  safeItems.forEach((item) => {
    if (!GRADED_KINDS.has(item.kind)) return;
    const hasCorrect = safeTriggers.some((tr) => {
      if (!tr) return false;
      const sourceMatches = tr.sourceItemId === item.id || tr.pluginBoxId === item.id;
      if (!sourceMatches) return false;
      if (tr.on === "onAnswer.correct" && tr.actionTargetItemId) return true;
      // Per-option triggers count too — the author has explicitly routed the
      // graded outcome path even if they didn't use the correct/wrong handle.
      if (tr.on === "onOptionPicked" && tr.actionTargetItemId) return true;
      return false;
    });
    if (!hasCorrect) {
      issues.push({
        code: "NO_CORRECT_PATH",
        severity: "warn",
        message: `Graded item "${item.kind}" (${item.id}) has no correct-answer route.`,
        targetItemId: item.id,
      });
    }
  });

  // 5. ORPHANED_BRANCHING_ALT — BranchingQuestion alternative with no actions.
  safeItems.forEach((item) => {
    if (item.kind !== "BranchingQuestion") return;
    const alts = (item.params && item.params.alternatives) || item.alternatives || [];
    alts.forEach((alt, ai) => {
      const actions = Array.isArray(alt.actions) ? alt.actions : [];
      if (actions.length === 0) {
        issues.push({
          code: "ORPHANED_BRANCHING_ALT",
          severity: "warn",
          message: `BranchingQuestion "${item.id}" alternative "${alt.label || `#${ai + 1}`}" has no actions.`,
          targetItemId: item.id,
          suggestedFix: {
            label: "Wire to End",
            actionCreator: fixOrphanedBranchingAlt,
          },
        });
      }
    });
  });

  return issues;
}

export default validateWorkflow;
